import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, Calendar, Plus, Trash2, Save, Check, X, Clock, DollarSign, Inbox } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { bookingApi } from '@/lib/bookingApi';
import { toast } from 'sonner';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DURATIONS = [30, 45, 60, 90];
const MODES = [
  { v: 'tutor_approval', l: 'Tutor approval required' },
  { v: 'instant_confirmed', l: 'Instant confirmed' },
  { v: 'admin_approval', l: 'Admin approval required' },
];
const MEL_TZ = 'Australia/Melbourne';
const fmtMel = (iso) => {
  try {
    return new Intl.DateTimeFormat('en-AU', {
      timeZone: MEL_TZ, weekday: 'short', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date(iso));
  } catch (_e) { return iso; }
};

const TutorSchedulePage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [tab, setTab] = useState('availability');
  const [av, setAv] = useState(null);
  const [loadingAv, setLoadingAv] = useState(true);
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loadingReq, setLoadingReq] = useState(false);

  const refresh = useCallback(async () => {
    setLoadingAv(true);
    try { setAv(await bookingApi.myAvailability()); } catch (_e) {} finally { setLoadingAv(false); }
  }, []);

  const refreshReq = useCallback(async () => {
    setLoadingReq(true);
    try { const r = await bookingApi.myRequests(); setRequests(r.items || []); } catch (_e) {} finally { setLoadingReq(false); }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/tutor/login', { replace: true }); return; }
    if ((user.role || '').toLowerCase() !== 'tutor') { navigate('/', { replace: true }); return; }
    refresh(); refreshReq();
  }, [loading, user, navigate, refresh, refreshReq]);

  const save = async () => {
    if (!av) return;
    setSaving(true);
    try {
      const cleaned = { ...av, weekly_rules: (av.weekly_rules || []).filter((r) => r.start && r.end) };
      await bookingApi.setMyAvailability(cleaned);
      toast.success('Availability saved');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const addRule = () => setAv((a) => ({ ...a, weekly_rules: [...(a.weekly_rules || []), { dow: 1, start: '09:00', end: '12:00' }] }));
  const removeRule = (i) => setAv((a) => ({ ...a, weekly_rules: a.weekly_rules.filter((_, j) => j !== i) }));
  const updateRule = (i, key, val) => setAv((a) => ({ ...a, weekly_rules: a.weekly_rules.map((r, j) => j === i ? { ...r, [key]: key === 'dow' ? Number(val) : val } : r) }));
  const addBlocked = (date) => { if (!date) return; setAv((a) => ({ ...a, blocked_dates: Array.from(new Set([...(a.blocked_dates || []), date])) })); };
  const removeBlocked = (date) => setAv((a) => ({ ...a, blocked_dates: (a.blocked_dates || []).filter((d) => d !== date) }));

  const respond = async (id, action, reason) => {
    try { await bookingApi.respond(id, { action, reason: reason || null }); toast.success(action === 'approve' ? 'Booking approved' : 'Booking declined'); refreshReq(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const pendingCount = requests.filter((r) => r.request_status === 'pending').length;

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-red-700" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="tutor-schedule-page">
      <header className="bg-black text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] font-bold">WINHOUSE TUTOR</div>
            <div className="text-sm font-bold">My Schedule</div>
          </div>
          <button onClick={() => navigate('/tutor/dashboard')} className="h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid="back-tutor"><ArrowLeft className="h-3 w-3" /> Dashboard</button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6 space-y-5">
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setTab('availability')} data-testid="tab-availability"
            className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${tab === 'availability' ? 'bg-black text-white' : 'bg-white border border-gray-200 text-gray-700'}`}>
            <Calendar className="h-3.5 w-3.5" /> Availability
          </button>
          <button onClick={() => setTab('requests')} data-testid="tab-requests"
            className={`h-9 px-4 rounded-full text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 ${tab === 'requests' ? 'bg-black text-white' : 'bg-white border border-gray-200 text-gray-700'}`}>
            <Inbox className="h-3.5 w-3.5" /> Booking requests
            {pendingCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-700 text-white text-[10px] font-bold">{pendingCount}</span>}
          </button>
        </div>

        {tab === 'availability' && (
          loadingAv ? <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div> : (
            <section className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-3">Settings</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Default duration</label>
                    <select value={av?.default_duration_min || 60} onChange={(e) => setAv((a) => ({ ...a, default_duration_min: Number(e.target.value) }))} className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm" data-testid="duration-default">
                      {DURATIONS.map((d) => <option key={d} value={d}>{d} min</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">Booking mode</label>
                    <select value={av?.booking_mode || 'tutor_approval'} onChange={(e) => setAv((a) => ({ ...a, booking_mode: e.target.value }))} className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm" data-testid="booking-mode">
                      {MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1 flex items-center gap-1"><DollarSign className="h-3 w-3" /> Hourly rate (AUD)</label>
                    <input type="number" value={av?.hourly_rate_aud ?? ''} onChange={(e) => setAv((a) => ({ ...a, hourly_rate_aud: e.target.value ? Number(e.target.value) : null }))} placeholder="e.g. 80" className="w-full h-10 px-3 rounded-lg border border-gray-200 bg-white text-sm" data-testid="hourly-rate" />
                  </div>
                </div>
                <p className="text-[10px] text-gray-500 mt-3">All times stored in UTC and displayed in Melbourne time across the platform.</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Weekly recurring availability</div>
                  <button onClick={addRule} className="h-8 px-3 rounded-full bg-black text-[#D3D86F] text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" data-testid="add-rule"><Plus className="h-3 w-3" /> Add slot</button>
                </div>
                {(av?.weekly_rules || []).length === 0 ? (
                  <div className="text-center text-xs text-gray-400 italic py-6" data-testid="no-rules">No weekly slots yet. Add at least one to receive bookings.</div>
                ) : (
                  <div className="space-y-2" data-testid="rules-list">
                    {av.weekly_rules.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg" data-testid={`rule-${i}`}>
                        <select value={r.dow} onChange={(e) => updateRule(i, 'dow', e.target.value)} className="h-9 px-2 rounded border border-gray-200 bg-white text-xs">
                          {DOW.map((d, idx) => <option key={d} value={idx}>{d}</option>)}
                        </select>
                        <input type="time" value={r.start} onChange={(e) => updateRule(i, 'start', e.target.value)} className="h-9 px-2 rounded border border-gray-200 bg-white text-xs" />
                        <span className="text-gray-400">to</span>
                        <input type="time" value={r.end} onChange={(e) => updateRule(i, 'end', e.target.value)} className="h-9 px-2 rounded border border-gray-200 bg-white text-xs" />
                        <button onClick={() => removeRule(i)} className="ml-auto h-8 w-8 rounded hover:bg-red-50 text-red-700 flex items-center justify-center" data-testid={`remove-rule-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-3">Blocked dates</div>
                <div className="flex gap-2 items-center flex-wrap mb-3">
                  <input type="date" onChange={(e) => { addBlocked(e.target.value); e.target.value = ''; }} className="h-9 px-2 rounded border border-gray-200 bg-white text-xs" data-testid="blocked-input" />
                  <span className="text-[10px] text-gray-400">Pick a date to block out (vacation, exam day, etc.)</span>
                </div>
                {(av?.blocked_dates || []).length === 0 ? (
                  <div className="text-[11px] text-gray-400 italic">No blocked dates.</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5" data-testid="blocked-list">
                    {av.blocked_dates.map((d) => (
                      <span key={d} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-900 text-[11px] font-mono" data-testid={`blocked-${d}`}>
                        {d} <button onClick={() => removeBlocked(d)} className="text-amber-700"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button onClick={save} disabled={saving} className="h-10 px-6 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-40" data-testid="save-availability">
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save schedule
                </button>
              </div>
            </section>
          )
        )}

        {tab === 'requests' && (
          <section className="space-y-3">
            {loadingReq ? <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div> :
              requests.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-2xl text-center text-xs text-gray-400 italic py-12" data-testid="no-requests">No booking requests yet.</div>
              ) : (
                <div className="space-y-3" data-testid="requests-list">
                  {requests.map((r) => <RequestCard key={r.booking_id} r={r} onApprove={() => respond(r.booking_id, 'approve')} onDecline={(reason) => respond(r.booking_id, 'decline', reason)} />)}
                </div>
              )}
          </section>
        )}
      </main>
    </div>
  );
};

const RequestCard = ({ r, onApprove, onDecline }) => {
  const [reason, setReason] = useState('');
  const [open, setOpen] = useState(false);
  const isPending = r.request_status === 'pending';
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid={`req-${r.booking_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-gray-900">{r.student_name} <span className="text-[10px] text-gray-500 font-normal">· {r.student_email}</span></div>
          <div className="text-[11px] text-gray-500 mt-0.5">{r.subject} · {r.program} · {r.lesson_type} · {r.duration_min} min</div>
          <div className="text-[11px] text-gray-700 mt-1 font-bold">{fmtMel(r.start_time)} (Melbourne)</div>
          {r.student_notes && <div className="text-[11px] text-gray-600 mt-2 p-2 bg-gray-50 rounded">"{r.student_notes}"</div>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusPill s={r.request_status} />
          {isPending && (
            <>
              <button onClick={onApprove} className="h-8 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" data-testid={`approve-${r.booking_id}`}><Check className="h-3 w-3" /> Approve</button>
              <button onClick={() => setOpen((o) => !o)} className="h-8 px-3 rounded-full bg-white border border-red-300 text-red-700 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" data-testid={`decline-${r.booking_id}`}><X className="h-3 w-3" /> Decline</button>
            </>
          )}
        </div>
      </div>
      {isPending && open && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap" data-testid={`decline-box-${r.booking_id}`}>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional reason (sent to student)" className="flex-1 h-9 px-3 rounded-lg border border-gray-200 bg-white text-xs" />
          <button onClick={() => { onDecline(reason); setOpen(false); setReason(''); }} className="h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-[10px] font-bold uppercase tracking-wider">Confirm decline</button>
        </div>
      )}
    </div>
  );
};

const StatusPill = ({ s }) => {
  const styles = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', declined: 'bg-red-100 text-red-800' }[s] || 'bg-gray-100 text-gray-700';
  return <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${styles}`}>{s || '—'}</span>;
};

export default TutorSchedulePage;
